/**
 * Customer notifications (DEV-100) — separate from the assessment.
 *
 * The triage decides; a confirmed product verdict AFFECTED creates one
 * Benachrichtigung per instance running the version. This page works through
 * them per product version × CVE: write the Security Advisory (short or CRA
 * template), review and edit the draft per customer, send it from the mail
 * client and stamp it as sent. The ENISA report can dock here later.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import Banner from '../components/Banner';
import Chip, { ChipList } from '../components/Chip';
import CveLink from '../components/CveLink';
import DataTable, { type DataTableColumn } from '../components/DataTable';
import EmptyState from '../components/EmptyState';
import Modal from '../components/Modal';
import PageHeader from '../components/PageHeader';
import Spinner, { PageSpinner } from '../components/Spinner';
import { useToast } from '../components/Toast';
import { errorMessage, fetchNotificationQueue, fetchVulnerabilityEvents } from '../lib/api';
import { useAuth } from '../lib/auth';
import { countLabel, fmtDate } from '../lib/format';
import { readOnlyNotice } from '../lib/permissions';
import type { NotificationQueueRow, VulnerabilityEventRow } from '../lib/types';

import { STATUS_LABELS, STATUS_TONE } from './triage/eventHelpers';
import NotificationDrafts from './triage/NotificationDrafts';
import { AdvisoryPanel, AttachmentsPanel } from './triage/TriageDocumentPanels';
import { useTriageDocuments } from './triage/useTriageDocuments';

import './triage/triage.css';

function productLabel(row: NotificationQueueRow): string {
  return [row.product_name, row.version_nr].filter(Boolean).join(' ') || `Produktversion #${row.version_id}`;
}

/** The component hit whose facts fill the advisory templates. */
function eventFor(row: NotificationQueueRow, events: readonly VulnerabilityEventRow[]): VulnerabilityEventRow | null {
  return events.find((event) => event.cve.id?.toUpperCase() === row.cve
    && event.komponente.id !== null && row.component_version_ids.includes(event.komponente.id))
    ?? events.find((event) => event.cve.id?.toUpperCase() === row.cve)
    ?? null;
}

type WorkspaceTab = 'advisory' | 'drafts';

function NotificationWorkspace({ row, event, canWrite, canSend, onClose, onChanged }: {
  row: NotificationQueueRow;
  event: VulnerabilityEventRow;
  canWrite: boolean;
  canSend: boolean;
  onClose: () => void;
  onChanged: () => void;
}): ReactNode {
  const toast = useToast();
  const documents = useTriageDocuments({ versionId: row.version_id, cve: row.cve });
  const [tab, setTab] = useState<WorkspaceTab>(row.documents?.advisory ? 'drafts' : 'advisory');
  const [draftsDirty, setDraftsDirty] = useState(false);
  const unsaved = canWrite && (documents.dirty || documents.queue.length > 0);

  async function save(): Promise<void> {
    try {
      await documents.save();
      toast.success('Security Advisory gespeichert.');
      onChanged();
    } catch {
      // The hook shows the German detail.
    }
  }

  function requestClose(): void {
    if ((unsaved || draftsDirty) && !window.confirm(
      unsaved ? 'Ungespeicherte Änderungen am Advisory verwerfen?' : 'Ungespeicherte Änderungen an den Entwürfen verwerfen?',
    )) return;
    onClose();
  }

  const tabs: Array<{ id: WorkspaceTab; label: string }> = [
    { id: 'advisory', label: 'Security Advisory' },
    { id: 'drafts', label: 'Entwürfe & Versand' },
  ];

  return (
    <Modal
      title={<>Benachrichtigung <span className="muted">/</span> <CveLink cve={row.cve} /></>}
      subtitle={`${productLabel(row)} · ${countLabel(row.customer_count, 'Kunde', 'Kunden')} · ${countLabel(row.instance_count, 'Instanz', 'Instanzen')}`}
      size="wide"
      dismissable={!documents.busy}
      onClose={requestClose}
      footer={<><div className="spacer" /><button type="button" className="ghost" onClick={requestClose}>Schließen</button></>}
    >
      <div className="triage-workspace">
        <div className="tabs triage-workspace-tabs" role="tablist" aria-label="Benachrichtigung">
          {tabs.map((item) => (
            <button key={item.id} type="button" role="tab" aria-selected={tab === item.id}
              id={`notification-tab-${item.id}`} aria-controls={`notification-panel-${item.id}`}
              tabIndex={tab === item.id ? 0 : -1} onClick={() => setTab(item.id)}>
              {item.label}
              {item.id === 'advisory' && unsaved ? <span className="triage-unsaved-dot" aria-label="Ungespeicherte Änderungen" /> : null}
            </button>
          ))}
        </div>
        {row.contradiction ? (
          <Banner kind="error">
            Die Produktbewertung lautet inzwischen „{STATUS_LABELS[row.status]}“. Versendete Benachrichtigungen bleiben bestehen.
          </Banner>
        ) : null}

        <div role="tabpanel" id="notification-panel-advisory" aria-labelledby="notification-tab-advisory" hidden={tab !== 'advisory'}>
          {documents.loading ? <p role="status"><Spinner /> Dokumente werden geladen…</p> : (
            <div className="assessment-documents">
              {documents.loadError ? <Banner kind="error">Dokumente konnten nicht geladen werden: {documents.loadError}</Banner> : null}
              {documents.error ? <Banner kind="error">{documents.error}</Banner> : null}
              {documents.documents ? (
                <>
                  <AdvisoryPanel event={event} state={documents} disabled={documents.busy} canWrite={canWrite} onSave={save} />
                  <AttachmentsPanel event={event} state={documents} disabled={documents.busy} canWrite={canWrite} />
                </>
              ) : null}
            </div>
          )}
        </div>

        <div role="tabpanel" id="notification-panel-drafts" aria-labelledby="notification-tab-drafts" hidden={tab !== 'drafts'}>
          {unsaved ? (
            <Banner kind="warn">Das Advisory hat ungespeicherte Änderungen. Die Entwürfe verwenden die gespeicherte Fassung.</Banner>
          ) : null}
          {tab === 'drafts' && documents.documents ? (
            <NotificationDrafts
              versionId={row.version_id}
              cve={row.cve}
              advisoryRevision={documents.documents.advisory.revision}
              canSend={canSend}
              onStamped={onChanged}
              onWriteAdvisory={() => setTab('advisory')}
              onDirtyChange={setDraftsDirty}
            />
          ) : null}
        </div>
      </div>
    </Modal>
  );
}

export function NotificationsPage(): ReactNode {
  const { has } = useAuth();
  const [params] = useSearchParams();
  const [rows, setRows] = useState<NotificationQueueRow[] | null>(null);
  const [events, setEvents] = useState<VulnerabilityEventRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState(params.get('q') ?? '');
  const [onlyOpen, setOnlyOpen] = useState(true);
  const [openKey, setOpenKey] = useState<string | null>(() => {
    const version = params.get('version');
    const cve = params.get('cve');
    return version && cve ? `${version}:${cve.toUpperCase()}` : null;
  });
  const canWrite = has('triage.write');
  const canSend = has('notification.send');

  const load = useCallback(async (): Promise<void> => {
    try {
      const [queue, triage] = await Promise.all([fetchNotificationQueue(), fetchVulnerabilityEvents()]);
      setRows(queue.rows);
      setEvents(triage.events);
      setError(null);
    } catch (err) {
      setError(errorMessage(err));
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const keyOf = (row: NotificationQueueRow): string => `${row.version_id}:${row.cve}`;
  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (rows ?? []).filter((row) => {
      if (onlyOpen && row.notifications.open === 0 && !row.contradiction && keyOf(row) !== openKey) return false;
      if (!needle) return true;
      return [row.cve, row.product_name, row.version_nr, ...row.components]
        .some((value) => value?.toLowerCase().includes(needle));
    });
  }, [rows, search, onlyOpen, openKey]);
  const openRow = rows?.find((row) => keyOf(row) === openKey) ?? null;
  const openEvent = openRow ? eventFor(openRow, events) : null;

  const columns: DataTableColumn<NotificationQueueRow>[] = [
    { key: 'cve', header: 'CVE', width: '15%', sortValue: (row) => row.cve, render: (row) => <CveLink cve={row.cve} /> },
    { key: 'product', header: 'Produktversion', sortValue: (row) => productLabel(row),
      render: (row) => (
        <div>
          <div className="kunde-name">{productLabel(row)} {row.custom ? <Chip tone="info">Kundenversion</Chip> : null}</div>
          <div className="prod">{row.components.join(', ') || '—'}</div>
        </div>
      ) },
    { key: 'kunden', header: 'Empfänger', width: '14%', sortValue: (row) => row.customer_count,
      render: (row) => <div>{countLabel(row.customer_count, 'Kunde', 'Kunden')}<div className="prod">{countLabel(row.instance_count, 'Instanz', 'Instanzen')}</div></div> },
    { key: 'advisory', header: 'Security Advisory', width: '15%', sortValue: (row) => Number(Boolean(row.documents?.advisory)),
      render: (row) => row.documents == null ? <span className="muted">unbekannt</span>
        : row.documents.advisory ? <Chip tone="good">gespeichert</Chip> : <Chip tone="warn">fehlt</Chip> },
    { key: 'versand', header: 'Versand', width: '18%', sortValue: (row) => row.notifications.open,
      render: (row) => (
        <div>
          <ChipList>
            {row.notifications.open ? <Chip tone="warn">{row.notifications.open} offen</Chip> : null}
            {row.notifications.sent ? <Chip tone="good">{row.notifications.sent} versendet</Chip> : null}
            {row.contradiction ? <Chip tone={STATUS_TONE[row.status]} title="Produktbewertung geändert">Widerspruch</Chip> : null}
          </ChipList>
          {row.next_deadline ? <div className="prod">Frist {fmtDate(row.next_deadline)}</div> : null}
        </div>
      ) },
    { key: 'aktion', header: 'Aktion', width: '12%',
      render: (row) => (
        <div className="triage-actions">
          <button type="button" className={row.notifications.open ? 'primary small' : 'ghost small'}
            onClick={(e) => { e.stopPropagation(); setOpenKey(keyOf(row)); }}>
            {row.notifications.open ? 'Benachrichtigen' : 'Öffnen'}
          </button>
        </div>
      ) },
  ];

  if (rows === null && error === null) return <PageSpinner text="Benachrichtigungen werden geladen…" />;

  return (
    <>
      <PageHeader title="Kundenbenachrichtigungen" sub="Je bestätigt betroffener Produktversion: Security Advisory verfassen, Entwürfe prüfen, versenden" />
      {error ? <Banner kind="error">{error}</Banner> : null}
      {!canSend ? <Banner kind="info">{readOnlyNotice('notification.send')}</Banner> : null}
      <section className="card">
        <div className="card-head table-head"><h2>Benachrichtigungen</h2></div>
        <div className="table-toolbar">
          <input type="search" value={search} placeholder="CVE, Produkt oder Komponente suchen…"
            aria-label="Benachrichtigungen filtern" onChange={(e) => setSearch(e.target.value)} />
          <label className="check">
            <input type="checkbox" checked={onlyOpen} onChange={(e) => setOnlyOpen(e.target.checked)} />
            Nur offene
          </label>
          <div className="table-toolbar-options">
            <span className="table-count" aria-live="polite">{countLabel(visible.length, 'Produktversion', 'Produktversionen')}</span>
          </div>
        </div>
        {(rows ?? []).length === 0 ? (
          <EmptyState title="Keine Benachrichtigungen">
            Benachrichtigungen entstehen, sobald eine Produktversion in der <Link to="/triage">Triage</Link> als „Betroffen“ bestätigt ist.
          </EmptyState>
        ) : (
          <DataTable rows={visible} columns={columns} rowKey={keyOf} pageSize={15} mobileBreakpoint={900}
            caption="Kundenbenachrichtigungen je Produktversion"
            emptyText="Keine offenen Benachrichtigungen. „Nur offene“ abwählen, um alle zu sehen." />
        )}
      </section>
      {openRow && openEvent ? (
        <NotificationWorkspace
          key={keyOf(openRow)}
          row={openRow}
          event={openEvent}
          canWrite={canWrite}
          canSend={canSend}
          onClose={() => setOpenKey(null)}
          onChanged={() => void load()}
        />
      ) : null}
    </>
  );
}

export default NotificationsPage;
